/**
 * Composer — message input for the V2 drawer. Autosizing textarea, Enter to
 * send / Shift+Enter for newline, "/" opens the existing SlashCommandMenu,
 * ScopeChips summarise the outgoing scope, and the map pick/draw toggle plus
 * DataSourcePicker sit on the toolbar. Send swaps to Cancel while running.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ChevronDownIcon,
  Loader2Icon,
  MousePointer2Icon,
  FileTextIcon,
  UploadIcon,
  PentagonIcon,
  SendHorizonalIcon,
  SlashIcon,
  SquareIcon,
  XIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import SlashCommandMenu from '../SlashCommandMenu';
import type { AICommand } from '../hooks/useAICommands';
import DataSourcePicker from './DataSourcePicker';
import ScopeChips, { buildScopeChips } from './ScopeChips';
import type { RealtimeSelection } from './types';
import {
  CHAT_UPLOAD_ACCEPT,
  filesFrom,
  type ChatFileIndexState,
  type PendingChatAttachment,
} from './chatAttachments';
import '../ChatDisasterGPT.css';

function fileChipStatus(
  item: PendingChatAttachment,
  indexState: ChatFileIndexState | null | undefined
): string {
  if (item.status === 'uploading') return 'Uploading…';
  if (item.status === 'error') return 'Failed';
  if (indexState === 'indexing') return 'Indexing…';
  if (indexState === 'unindexed') return 'Not searchable';
  return 'Ready';
}

export const COMPOSER_PLACEHOLDER =
  'Message PRATUS AI Chat...  (type / for commands)';

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  running: boolean;
  onCancel: () => void;
  /** Validation hint shown under the input (e.g. SITREP needs a selection). */
  hint?: string;
  commands: AICommand[];
  selections: RealtimeSelection[];
  setSelections: React.Dispatch<React.SetStateAction<RealtimeSelection[]>>;
  incidentName?: string;
  aiLayerPickMode?: boolean;
  onToggleAiLayerPickMode?: () => void;
  /** Draw an area on the map for AI to consider. */
  onStartAiAreaSelect?: () => void;
  aiAreaSelectActive?: boolean;
  /** Zoom the map to the selected area (Map Area chip click). */
  onZoomToMapArea?: () => void;
  /** Increment to focus the textarea (e.g. after a queued skill prompt). */
  focusNonce?: number;
  /** Item a pending form draft will update, shown under the textarea. */
  draftLabel?: string;
  /** Drops the pending form draft so the next send goes to the agent. */
  onClearDraft?: () => void;
  /**
   * Files picked for the next message, uploaded to Org Files. Omit `onAddFiles`
   * to hide upload.
   */
  attachments?: PendingChatAttachment[];
  /** Whether PRATUS AI can search each uploaded file yet, by pending id. */
  attachmentIndexStates?: Record<string, ChatFileIndexState | null>;
  onAddFiles?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
}

const Composer: React.FC<ComposerProps> = ({
  value,
  onChange,
  onSubmit,
  canSubmit,
  running,
  onCancel,
  hint,
  commands,
  selections,
  setSelections,
  incidentName,
  aiLayerPickMode = false,
  onToggleAiLayerPickMode,
  onStartAiAreaSelect,
  aiAreaSelectActive = false,
  onZoomToMapArea,
  focusNonce = 0,
  draftLabel,
  onClearDraft,
  attachments = [],
  attachmentIndexStates = {},
  onAddFiles,
  onRemoveAttachment,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const canAttach = Boolean(onAddFiles) && !running;

  const dropHandlers = canAttach
    ? {
        onDragOver: (event: React.DragEvent) => {
          if (!Array.from(event.dataTransfer.types).includes('Files')) return;
          event.preventDefault();
          setDragging(true);
        },
        onDragLeave: (event: React.DragEvent) => {
          if (event.currentTarget.contains(event.relatedTarget as Node)) return;
          setDragging(false);
        },
        onDrop: (event: React.DragEvent) => {
          setDragging(false);
          const files = Array.from(event.dataTransfer.files ?? []);
          if (files.length === 0) return;
          event.preventDefault();
          onAddFiles?.(files);
        },
      }
    : {};

  const onPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach) return;
    const files = filesFrom(event.clipboardData);
    if (files.length === 0) return;
    event.preventDefault();
    onAddFiles?.(files);
  };
  const selectActive = aiLayerPickMode || aiAreaSelectActive;

  const chips = useMemo(
    () =>
      buildScopeChips({
        selections,
        incidentName,
        setSelections,
        onSelectMapArea: onZoomToMapArea,
      }),
    [selections, incidentName, setSelections, onZoomToMapArea]
  );

  const focusEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    textareaRef.current?.focus();
  }, []);

  const selectCommand = useCallback(
    (cmd: AICommand) => {
      onChange(`${cmd.command} `);
      setMenuOpen(false);
      focusEnd();
    },
    [onChange, focusEnd]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === '/' && value.length === 0 && commands.length > 0) {
      e.preventDefault();
      setMenuOpen(true);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  // Refocus the input when a run finishes so the user can keep typing.
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) textareaRef.current?.focus();
    wasRunning.current = running;
  }, [running]);

  useEffect(() => {
    if (!focusNonce) return;
    const id = window.setTimeout(() => focusEnd(), 50);
    return () => window.clearTimeout(id);
  }, [focusNonce, focusEnd]);

  return (
    <div
      className={cn(
        'tw:relative tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:bg-background tw:px-3 tw:pb-3 tw:pt-2',
        dragging && 'tw:ring-2 tw:ring-primary tw:ring-inset'
      )}
      data-testid="ai-composer"
      data-tour="pratus-ai-composer"
      {...dropHandlers}
    >
      {dragging ? (
        <div className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-10 tw:flex tw:items-center tw:justify-center tw:bg-background/80 tw:text-sm tw:font-medium tw:text-foreground">
          Drop files to upload them to Org Files for PRATUS AI
        </div>
      ) : null}
      <ScopeChips chips={chips} disabled={running} />

      {attachments.length > 0 ? (
        <ul
          className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:gap-2 tw:p-0"
          aria-label="Uploaded files"
        >
          {attachments.map((item) =>
            item.previewUrl ? (
              <li
                key={item.id}
                className={cn(
                  'tw:relative tw:size-16 tw:overflow-hidden tw:rounded-md tw:border tw:border-border',
                  item.status === 'error' && 'tw:border-destructive'
                )}
                title={item.name}
              >
                <img
                  src={item.previewUrl}
                  alt={item.name}
                  className={cn(
                    'tw:size-full tw:object-cover',
                    item.status !== 'ready' && 'tw:opacity-50'
                  )}
                />
                {item.status === 'uploading' ? (
                  <Loader2Icon className="tw:absolute tw:inset-0 tw:m-auto tw:size-5 tw:animate-spin tw:text-foreground" />
                ) : null}
                <Button
                  variant="secondary"
                  size="icon-xs"
                  className="tw:absolute tw:top-0.5 tw:right-0.5"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => onRemoveAttachment?.(item.id)}
                >
                  <XIcon />
                </Button>
              </li>
            ) : (
              <li
                key={item.id}
                data-testid="ai-upload-file-chip"
                className={cn(
                  'tw:flex tw:h-8 tw:max-w-60 tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-border tw:pl-2 tw:pr-0.5 tw:text-xs',
                  item.status === 'error' && 'tw:border-destructive'
                )}
                title={item.name}
              >
                {item.status === 'uploading' ? (
                  <Loader2Icon className="tw:size-3.5 tw:shrink-0 tw:animate-spin" />
                ) : (
                  <FileTextIcon className="tw:size-3.5 tw:shrink-0" />
                )}
                <span className="tw:min-w-0 tw:truncate">{item.name}</span>
                <span className="tw:shrink-0 tw:text-muted-foreground">
                  {fileChipStatus(item, attachmentIndexStates[item.id])}
                </span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => onRemoveAttachment?.(item.id)}
                >
                  <XIcon />
                </Button>
              </li>
            )
          )}
        </ul>
      ) : null}
      {Object.values(attachmentIndexStates).includes('indexing') ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          Indexing uploaded files. PRATUS AI can search them once indexing
          finishes; you can send now.
        </p>
      ) : null}

      <div className="tw:relative">
        {menuOpen && commands.length > 0 && (
          <SlashCommandMenu
            commands={commands}
            onSelect={selectCommand}
            onClose={closeMenu}
          />
        )}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={COMPOSER_PLACEHOLDER}
          aria-label="Message PRATUS AI"
          disabled={running}
          rows={1}
          className="tw:max-h-48 tw:min-h-10 tw:resize-none tw:py-2.5"
        />
      </div>

      {draftLabel ? (
        <Badge
          variant="secondary"
          data-testid="ai-draft-target"
          className="tw:h-auto tw:max-w-full tw:justify-start tw:gap-1 tw:whitespace-normal tw:border-violet-400/70! tw:bg-violet-500/15! tw:text-left tw:text-violet-200!"
        >
          {draftLabel}
          {onClearDraft ? (
            <button
              type="button"
              aria-label="Clear form draft"
              data-testid="ai-draft-target-clear"
              className="tw:ml-0.5 tw:inline-flex tw:rounded-sm tw:text-violet-200 tw:hover:text-white"
              onClick={onClearDraft}
            >
              <XIcon className="tw:size-3" aria-hidden />
            </button>
          ) : null}
        </Badge>
      ) : null}

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="outline"
                  size="icon-sm"
                  id="dgpt-command-trigger"
                  aria-label="Browse commands"
                  aria-expanded={menuOpen}
                  disabled={running || commands.length === 0}
                  onClick={() => setMenuOpen((o) => !o)}
                />
              }
            >
              <SlashIcon />
            </TooltipTrigger>
            <TooltipContent className="dark">Browse commands</TooltipContent>
          </Tooltip>
          {onAddFiles ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Upload file"
                    data-testid="ai-upload-file"
                    data-tour="pratus-ai-upload"
                    disabled={!canAttach}
                    onClick={() => fileInputRef.current?.click()}
                  />
                }
              >
                <UploadIcon />
              </TooltipTrigger>
              <TooltipContent className="dark">
                Upload files to Org Files for PRATUS AI (or drop / paste them)
              </TooltipContent>
            </Tooltip>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept={CHAT_UPLOAD_ACCEPT}
            multiple
            hidden
            data-testid="ai-upload-file-input"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (files.length > 0) onAddFiles?.(files);
            }}
          />
          {onToggleAiLayerPickMode && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant={selectActive ? 'default' : 'outline'}
                    size="sm"
                    aria-label="Select context for AI"
                    aria-pressed={selectActive}
                    disabled={running}
                    data-testid="ai-select-trigger"
                    data-tour="pratus-ai-select"
                    className="tw:gap-1 tw:px-2"
                  />
                }
              >
                {aiAreaSelectActive ? <PentagonIcon /> : <MousePointer2Icon />}
                {aiAreaSelectActive
                  ? 'Drawing area… (Esc to cancel)'
                  : 'Select'}
                <ChevronDownIcon className="tw:size-3.5 tw:opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="dark tw:w-72">
                <DropdownMenuItem
                  data-testid="ai-select-item"
                  onClick={onToggleAiLayerPickMode}
                  className="tw:h-auto tw:items-start tw:py-2"
                >
                  <MousePointer2Icon className="tw:mt-0.5" />
                  <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5 tw:leading-snug">
                    <span className="tw:font-medium">
                      {aiLayerPickMode && !aiAreaSelectActive
                        ? 'Stop selecting items'
                        : 'Select Item'}
                    </span>
                    <span className="tw:whitespace-normal tw:text-xs tw:text-muted-foreground">
                      Click a map layer, form card, or form box
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="ai-select-area"
                  disabled={!onStartAiAreaSelect || aiAreaSelectActive}
                  onClick={onStartAiAreaSelect}
                  className="tw:h-auto tw:items-start tw:py-2"
                >
                  <PentagonIcon className="tw:mt-0.5" />
                  <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5 tw:leading-snug">
                    <span className="tw:font-medium">Select Area</span>
                    <span className="tw:whitespace-normal tw:text-xs tw:text-muted-foreground">
                      Draw an area; AI considers everything inside it
                    </span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DataSourcePicker
            selections={selections}
            setSelections={setSelections}
            disabled={running}
          />
        </div>

        {running ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            aria-label="Cancel current run"
          >
            <SquareIcon /> Cancel
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onSubmit}
            disabled={!canSubmit}
            aria-label="Send"
            data-testid="ai-send"
          >
            Send <SendHorizonalIcon />
          </Button>
        )}
      </div>

      {hint && (
        <p className={cn('tw:m-0 tw:text-xs tw:text-warning')} role="status">
          {hint}
        </p>
      )}
    </div>
  );
};

export default Composer;
